import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { SectorError, type SectorRepository } from '../application/SectorRepository'
import {
  classificationSchema,
  draftValidationSchema,
  publicationContentSchema,
  sectorHistorySchema,
  leadSectorBriefSchema,
  leadSectorSummarySchema,
  publicationPreviewSchema,
  publishResultSchema,
  routeSectorsSchema,
  sectorWorkspaceSchema,
  sectorWriteResultSchema,
  serviceLocationSectorSchema,
} from '../domain/sector'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Code d'erreur stable (`SECTOR_DRAFT_CONFLICT`, …), jamais le message brut. */
function rpcErrorCode(message: string): string {
  return /(SECTOR|AVAILABILITY|SERVICE_LOCATION|LEAD)_[A-Z_]+/.exec(message)?.[0] ?? 'UNEXPECTED'
}

/** Identifiants par requête : l'URL de PostgREST reste courte. */
const leadIdsPerRequest = 150

const leadSectorRowSchema = z.object({
  id: z.string(),
  sector_id: z.string().nullable(),
  sector_status: leadSectorBriefSchema.shape.status,
})

/** Aucun repli sur des fixtures : un faux secteur ou une fausse disponibilité se liraient comme des faits. */
export function createSupabaseSectorRepository(client: AppSupabaseClient): SectorRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function rpc<T>(
    name: string,
    args: Record<string, unknown> | undefined,
    schema: z.ZodType<T>,
  ): Promise<T> {
    let response: RpcResponse
    try {
      response = await rpcClient.rpc(name, args)
    } catch {
      throw new SectorError('NETWORK')
    }
    if (response.error) throw new SectorError(rpcErrorCode(response.error.message))
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new SectorError('RESPONSE_INVALID')
    return parsed.data
  }

  const write = (name: string, command: object) => rpc(name, { p_command: command }, sectorWriteResultSchema)

  return {
    getWorkspace: () => rpc('service_sectors_workspace_v1', undefined, sectorWorkspaceSchema),
    createSector: (command) => write('service_sector_create_v1', command),
    updateSector: (command) => write('service_sector_update_v1', command),
    setSectorArchived: (command) => write('service_sector_set_archived_v1', command),
    saveArea: (command) => write('service_sector_save_area_v1', command),
    saveAvailability: (command) => write('service_sector_save_availability_v1', command),
    getDraftValidation: () => rpc('service_sector_draft_validation_v1', undefined, draftValidationSchema),
    copyReference: (command) => write('service_sector_copy_reference_v1', command),
    classifyPoint: (longitude, latitude, quality, source) =>
      rpc(
        'service_sector_classify_point_v1',
        { p_longitude: longitude, p_latitude: latitude, p_position_quality: quality, p_source: source },
        classificationSchema,
      ),
    getServiceLocations: (source, season) =>
      rpc(
        'service_sector_locations_v1',
        { p_source: source, p_season: season ?? null },
        z.array(serviceLocationSectorSchema),
      ),
    getRouteSectors: (source, season) =>
      rpc(
        'service_sector_routes_v1',
        { p_source: source, p_season: season ?? null },
        z.array(routeSectorsSchema),
      ),
    getPublicationPreview: () =>
      rpc('service_sector_publication_preview_v1', undefined, publicationPreviewSchema),
    publish: (command) => rpc('service_sector_publish_v1', { p_command: command }, publishResultSchema),
    getHistory: () => rpc('service_sector_history_v1', undefined, sectorHistorySchema),
    getPublication: (publicationId) =>
      rpc(
        'service_sector_publication_read_v1',
        { p_publication_id: publicationId },
        publicationContentSchema,
      ),
    restorePublication: (command) => write('service_sector_restore_v1', command),
    getLeadSector: (leadId) => rpc('lead_sector_summary_v1', { p_lead_id: leadId }, leadSectorSummarySchema),
    // Colonnes de `leads` sous la politique `lead.read`; les noms viennent de l'espace de travail.
    async getLeadSectors(leadIds) {
      const unique = [...new Set(leadIds)]
      const chunks: string[][] = []
      for (let start = 0; start < unique.length; start += leadIdsPerRequest) {
        chunks.push(unique.slice(start, start + leadIdsPerRequest))
      }
      const responses = await Promise.all(
        chunks.map(async (chunk): Promise<RpcResponse> => {
          try {
            return await rpcClient.from('leads').select('id,sector_id,sector_status').in('id', chunk)
          } catch {
            throw new SectorError('NETWORK')
          }
        }),
      )
      return responses.flatMap((response) => {
        if (response.error) throw new SectorError('UNEXPECTED')
        const parsed = z.array(leadSectorRowSchema).safeParse(response.data)
        if (!parsed.success) throw new SectorError('RESPONSE_INVALID')
        return parsed.data.map((row) => ({
          leadId: row.id,
          sectorId: row.sector_id,
          status: row.sector_status,
        }))
      })
    },
    async recheckLeadSector(leadId) {
      await rpc('lead_sector_recheck_v1', { p_lead_id: leadId }, z.unknown())
    },
    async setLeadSector(command) {
      await rpc('lead_sector_set_v1', { p_command: command }, z.unknown())
    },
    async setLocationOverride(command) {
      await rpc('service_location_sector_override_set_v1', { p_command: command }, z.unknown())
    },
  }
}
